// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Contact from "./contact.js"

const modelClassData = {
  "attributes": {
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "phone_number": {
      "column": {
        "default": null,
        "name": "phone_number",
        "null": false,
        "type": "string"
      },
      "name": "phone_number",
      "selected_by_default": null,
      "translated": null
    },
    "primary": {
      "column": {
        "default": false,
        "name": "primary",
        "null": false,
        "type": "boolean"
      },
      "name": "primary",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "PhoneNumber",
  "collectionKey": "phone_numbers",
  "collectionName": "phone_numbers",
  "i18nKey": "phone_number",
  "camelizedLower": "phoneNumber",
  "name": "PhoneNumber",
  "nameDasherized": "phone-number",
  "pluralName": "phone_numbers",
  "ransackable_associations": [
    {
      "className": "CallListEntity",
      "collectionName": "call_list_entities",
      "foreignKey": "phone_number_id",
      "name": "call_list_entities",
      "macro": "has_many",
      "resource_name": "CallListEntity",
      "through": null
    },
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "contact_id",
      "name": "contact",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "contact_id",
      "column": {
        "default": null,
        "name": "contact_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "old_contact_id",
      "column": {
        "default": null,
        "name": "old_contact_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "phone_number",
      "column": {
        "default": null,
        "name": "phone_number",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "primary",
      "column": {
        "default": false,
        "name": "primary",
        "null": false,
        "type": "boolean"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "contact_id",
      "name": "contact",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    }
  ],
  "paramKey": "phone_number",
  "primaryKey": "id"
}

/** Frontend model for PhoneNumber. */
export default class PhoneNumber extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  phoneNumber() {
    return this.readAttributeUnderscore("phone_number")
  }

  /** @returns {boolean} */
  hasPhoneNumber() {
    const value = this.phoneNumber()

    return this._isPresent(value)
  }

  /** @returns {boolean} */
  primary() {
    return this.readAttributeUnderscore("primary")
  }

  /** @returns {boolean} */
  hasPrimary() {
    const value = this.primary()

    return this._isPresent(value)
  }

  /** @returns {import("./contact.js").default | null} */
  contact() {
    return this._readBelongsToReflection({modelClass: Contact, reflectionName: "contact"})
  }

  /** @returns {Promise<import("./contact.js").default | null>} */
  loadContact() {
    if (!("contactId" in this)) throw new Error("Foreign key method wasn't defined: contactId")

    const id = this.contactId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "contact",
        model: this,
        modelClass: Contact
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./contact.js").default | null>} */
  ensureContactLoaded() {
    return this.ensureAssociationLoaded("contact")
  }
}
